/**
 * BracketSimulator Page
 *
 * Main page for the NCAA Tournament Bracket Simulator.
 * Handles bracket creation, loading, auto-fill, picks, sharing, and analysis.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toPng } from 'html-to-image';
import BracketView from '../components/bracket/BracketView';
import BracketToolbar from '../components/bracket/BracketToolbar';
import BracketExportImage from '../components/bracket/BracketExportImage';
import MatchupAnalyzerModal from '../components/bracket/MatchupAnalyzerModal';
import MascotBattleModal from '../components/bracket/MascotBattleModal';
import SimulationPanel from '../components/bracket/SimulationPanel';
import PremiumGateModal from '../components/common/PremiumGateModal';
import { useAuth } from '../contexts/AuthContext';
import {
  getTournamentField,
  createBracket,
  listBrackets,
  getBracket,
  getBracketPredictions,
  autoFillBracket,
  makePick,
  shareBracket,
  deleteBracket,
  cloneBracket,
} from '../services/bracketService';

const CURRENT_SEASON = 2026;

export default function BracketSimulator() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { userProfile } = useAuth();
  const isPremium = userProfile?.subscription_tier === 'premium' || userProfile?.subscription_tier === 'pro';

  // Data state
  const [entries, setEntries] = useState([]);
  const [bracket, setBracket] = useState(null);
  const [userBrackets, setUserBrackets] = useState([]);
  const [predictions, setPredictions] = useState({}); // keyed by "round-slot"

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAutoFilling, setIsAutoFilling] = useState(false);
  const [showBracketList, setShowBracketList] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportPreview, setShowExportPreview] = useState(false);
  const exportRef = useRef(null);
  const [analyzerState, setAnalyzerState] = useState(null); // { team1Id, team2Id, roundNumber, autoKnewton? }
  const [battleState, setBattleState] = useState(null); // { team1Id, team2Id }
  const [showSimulation, setShowSimulation] = useState(false);
  const [showPremiumGate, setShowPremiumGate] = useState(false);
  const pickQueueRef = useRef(Promise.resolve()); // Serializes pick operations

  // Load tournament field + user brackets on mount
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load tournament field
      const field = await getTournamentField(CURRENT_SEASON);
      setEntries(field.entries || []);

      // Load user brackets
      const brackets = await listBrackets(CURRENT_SEASON);
      setUserBrackets(brackets);

      // Check if there's a bracket_id in URL params
      const bracketIdParam = searchParams.get('id');
      if (bracketIdParam) {
        const b = await getBracket(parseInt(bracketIdParam));
        setBracket(b);
      } else if (brackets.length > 0) {
        // Load most recent bracket
        const b = await getBracket(brackets[0].id);
        setBracket(b);
      } else {
        setShowBracketList(true);
      }
    } catch (err) {
      console.error('Error loading bracket data:', err);
      if (err.response?.status === 404) {
        // No tournament entries yet - that's fine
        setError('Tournament field not available yet. Check back after Selection Sunday!');
      } else {
        setError(err.response?.data?.detail || 'Failed to load bracket data');
      }
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Load predictions whenever bracket changes
  useEffect(() => {
    if (!bracket?.id) {
      setPredictions({});
      return;
    }
    let cancelled = false;
    getBracketPredictions(bracket.id, CURRENT_SEASON)
      .then(data => {
        if (cancelled) return;
        const map = {};
        (data.predictions || []).forEach(p => {
          map[`${p.round_number}-${p.game_slot}`] = p;
        });
        setPredictions(map);
      })
      .catch(err => {
        if (!cancelled) console.error('Failed to load predictions:', err);
      });
    return () => { cancelled = true; };
  }, [bracket?.id, bracket?.picks?.length]);

  const handleCreateBracket = async (name = 'My Bracket', strategy = 'manual') => {
    try {
      // Auto-suffix to avoid unique constraint on (user_id, season, bracket_name)
      let bracketName = name;
      const existingNames = new Set(userBrackets.map(b => b.bracket_name));
      if (existingNames.has(bracketName)) {
        let n = 2;
        while (existingNames.has(`${name} ${n}`)) n++;
        bracketName = `${name} ${n}`;
      }

      const newBracket = await createBracket({
        bracket_name: bracketName,
        strategy,
        season: CURRENT_SEASON,
      });
      setBracket(newBracket);
      setShowBracketList(false);
      setSearchParams({ id: newBracket.id });

      // Refresh list
      const brackets = await listBrackets(CURRENT_SEASON);
      setUserBrackets(brackets);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create bracket');
    }
  };

  const handleLoadBracket = async (bracketId) => {
    try {
      setLoading(true);
      const b = await getBracket(bracketId);
      setBracket(b);
      setShowBracketList(false);
      setSearchParams({ id: bracketId });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load bracket');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBracket = async (bracketId) => {
    try {
      await deleteBracket(bracketId);
      const brackets = await listBrackets(CURRENT_SEASON);
      setUserBrackets(brackets);
      if (bracket?.id === bracketId) {
        setBracket(null);
        setShowBracketList(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete bracket');
    }
  };

  const handleCloneBracket = async (bracketId) => {
    try {
      const cloned = await cloneBracket(bracketId);
      setBracket(cloned);
      setShowBracketList(false);
      setSearchParams({ id: cloned.id });
      const brackets = await listBrackets(CURRENT_SEASON);
      setUserBrackets(brackets);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to clone bracket');
    }
  };

  const handleAutoFill = async (strategy, customParams = null) => {
    if (!bracket) return;
    try {
      setIsAutoFilling(true);
      await autoFillBracket(bracket.id, strategy, customParams);
      // Re-fetch the full bracket to get all picks with entry data
      const updated = await getBracket(bracket.id);
      setBracket(updated);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to auto-fill bracket');
    } finally {
      setIsAutoFilling(false);
    }
  };

  const handlePickWinner = useCallback((roundNumber, gameSlot, winnerEntryId) => {
    if (!bracket) return;

    // Optimistic update: apply pick locally for instant feedback
    setBracket(prev => {
      if (!prev) return prev;
      const updatedPicks = prev.picks.map(p => {
        if (p.round_number === roundNumber && p.game_slot === gameSlot) {
          return { ...p, winner_entry_id: winnerEntryId };
        }
        return p;
      });

      // Cascade: place winner into next round's team slot
      if (roundNumber < 6) {
        const nextRound = roundNumber + 1;
        const nextSlot = Math.ceil(gameSlot / 2);
        const isTopGame = gameSlot % 2 === 1;
        const field = isTopGame ? 'team1_entry_id' : 'team2_entry_id';

        const existingNext = updatedPicks.find(
          p => p.round_number === nextRound && p.game_slot === nextSlot
        );
        if (existingNext) {
          Object.assign(existingNext, { [field]: winnerEntryId });
        } else {
          updatedPicks.push({
            round_number: nextRound,
            game_slot: nextSlot,
            team1_entry_id: isTopGame ? winnerEntryId : null,
            team2_entry_id: !isTopGame ? winnerEntryId : null,
            winner_entry_id: null,
          });
        }
      }

      return { ...prev, picks: updatedPicks };
    });

    // Serialize backend calls to prevent race conditions in cascade logic.
    // Each pick must complete before the next starts, otherwise concurrent
    // clear_downstream + advance_winner operations corrupt R2+ team slots.
    const bracketId = bracket.id;
    pickQueueRef.current = pickQueueRef.current.then(async () => {
      try {
        await makePick(bracketId, {
          round_number: roundNumber,
          game_slot: gameSlot,
          winner_entry_id: winnerEntryId,
        });
        // Reload to get cascade clearing and server-computed state
        const updated = await getBracket(bracketId);
        setBracket(updated);
      } catch (err) {
        console.error('Error making pick:', err);
        setError(err.response?.data?.detail || 'Failed to save pick');
        // Revert on error
        const reverted = await getBracket(bracketId);
        setBracket(reverted);
      }
    });
  }, [bracket]);

  const handleShare = async () => {
    if (!bracket) return;
    try {
      const result = await shareBracket(bracket.id);
      const shareUrl = `${window.location.origin}/bracket?share=${result.share_code}`;
      await navigator.clipboard.writeText(shareUrl);
      // Brief success indication
      setError(null);
      alert(`Share link copied to clipboard!\n${shareUrl}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate share link');
    }
  };

  const handleReset = async () => {
    if (!bracket || !window.confirm('Reset this bracket? All picks will be cleared.')) return;
    try {
      await deleteBracket(bracket.id);
      const newBracket = await createBracket({
        bracket_name: bracket.bracket_name,
        strategy: 'manual',
        season: CURRENT_SEASON,
      });
      setBracket(newBracket);
      setSearchParams({ id: newBracket.id });
      const brackets = await listBrackets(CURRENT_SEASON);
      setUserBrackets(brackets);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset bracket');
    }
  };

  const handleExportImage = useCallback(async () => {
    if (!bracket) return;
    try {
      setIsExporting(true);
      setShowExportPreview(true);

      // Wait for the export component to render (longer on mobile)
      await new Promise(resolve => setTimeout(resolve, 500));

      if (!exportRef.current) {
        setError('Export failed — could not render bracket image');
        return;
      }

      const dataUrl = await toPng(exportRef.current, {
        quality: 0.95,
        pixelRatio: 2,
        backgroundColor: '#0f172a',
      });

      const fileName = `${bracket.bracket_name?.replace(/\s+/g, '_') || 'bracket'}_${bracket.season || 2026}.png`;

      // Mobile browsers don't support link.click() for downloads.
      // Use Share API if available, otherwise open in new tab.
      if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) {
        try {
          const blob = await (await fetch(dataUrl)).blob();
          const file = new File([blob], fileName, { type: 'image/png' });
          await navigator.share({ files: [file], title: bracket.bracket_name || 'My Bracket' });
        } catch (shareErr) {
          // Share cancelled or unsupported — open in new tab as fallback
          if (shareErr.name !== 'AbortError') {
            window.open(dataUrl, '_blank');
          }
        }
      } else {
        const link = document.createElement('a');
        link.download = fileName;
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Error exporting bracket image:', err);
      setError('Failed to export bracket image');
    } finally {
      setIsExporting(false);
      setShowExportPreview(false);
    }
  }, [bracket]);

  const handleAnalyze = useCallback((team1Id, team2Id, roundNumber) => {
    setAnalyzerState({ team1Id, team2Id, roundNumber });
  }, []);

  const handleAskKnewton = useCallback((team1, team2, roundNumber) => {
    setAnalyzerState({ team1Id: team1.id, team2Id: team2.id, roundNumber, autoKnewton: true });
  }, []);

  const handleMascotBattle = useCallback((team1Id, team2Id) => {
    setBattleState({ team1Id, team2Id });
  }, []);

  const handleBattlePick = useCallback((winnerEntryId) => {
    if (!battleState || !bracket) return;
    const pick = bracket.picks.find(
      p => (p.team1_entry_id === battleState.team1Id && p.team2_entry_id === battleState.team2Id) ||
           (p.team1_entry_id === battleState.team2Id && p.team2_entry_id === battleState.team1Id)
    );
    if (pick) {
      handlePickWinner(pick.round_number, pick.game_slot, winnerEntryId);
    }
  }, [battleState, bracket, handlePickWinner]);

  const handleAnalyzerPick = useCallback((winnerEntryId) => {
    if (!analyzerState || !bracket) return;
    // Find the game slot from the current picks
    const pick = bracket.picks.find(
      p => p.round_number === analyzerState.roundNumber &&
        ((p.team1_entry_id === analyzerState.team1Id && p.team2_entry_id === analyzerState.team2Id) ||
         (p.team1_entry_id === analyzerState.team2Id && p.team2_entry_id === analyzerState.team1Id))
    );
    if (pick) {
      handlePickWinner(pick.round_number, pick.game_slot, winnerEntryId);
    }
  }, [analyzerState, bracket, handlePickWinner]);

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6 p-4 lg:p-6">
        <PageHeader />
        <div className="flex items-center justify-center py-24">
          <div className="text-center space-y-3">
            <div className="animate-spin w-10 h-10 border-3 border-accent-blue border-t-transparent rounded-full mx-auto" />
            <p className="text-sm text-text-secondary">Loading tournament data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state (but still show UI)
  // No entries = tournament not set up yet
  if (entries.length === 0) {
    return (
      <div className="space-y-6 p-4 lg:p-6">
        <PageHeader />
        <div className="glass-card p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-blue/10 flex items-center justify-center">
            <svg className="w-8 h-8 text-accent-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-text-primary mb-2">Tournament Field Not Available Yet</h3>
          <p className="text-sm text-text-secondary max-w-md mx-auto">
            The bracket simulator will be ready after Selection Sunday when the tournament field is announced.
            Check back soon!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 lg:p-6">
      <PageHeader />

      {/* Error banner */}
      {error && (
        <div className="bg-danger/10 border border-danger/30 rounded-lg px-4 py-2 flex items-center justify-between">
          <span className="text-sm text-danger">{error}</span>
          <button onClick={() => setError(null)} className="text-danger/60 hover:text-danger">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Bracket list / create */}
      {showBracketList || !bracket ? (
        <BracketListPanel
          brackets={userBrackets}
          onCreate={handleCreateBracket}
          onLoad={handleLoadBracket}
          onDelete={handleDeleteBracket}
          onClone={handleCloneBracket}
          onBack={bracket ? () => setShowBracketList(false) : null}
        />
      ) : (
        <>
          {/* Bracket switcher + confidence badge */}
          <div className="flex items-center gap-2">
            {userBrackets.length > 1 && (
              <select
                value={bracket.id}
                onChange={(e) => handleLoadBracket(parseInt(e.target.value))}
                className="input-glow text-xs py-1 px-2 bg-bg-card"
              >
                {userBrackets.map(b => (
                  <option key={b.id} value={b.id}>{b.bracket_name}</option>
                ))}
              </select>
            )}
            {userBrackets.length === 1 && (
              <span className="text-xs font-medium text-text-secondary">{bracket.bracket_name}</span>
            )}
            {bracket.confidence_score != null && (
              <ConfidenceBadge score={bracket.confidence_score} />
            )}
            <button
              onClick={() => handleCloneBracket(bracket.id)}
              className="p-1 text-text-tertiary hover:text-accent-purple transition-colors"
              title="Clone bracket"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
            <button
              onClick={() => setShowBracketList(true)}
              className="text-xs text-text-tertiary hover:text-accent-blue transition-colors flex items-center gap-1"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
              </svg>
              My Brackets
            </button>
          </div>

          {/* Toolbar */}
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <BracketToolbar
                bracket={bracket}
                onAutoFill={handleAutoFill}
                onShare={handleShare}
                onExportImage={handleExportImage}
                onReset={handleReset}
                isAutoFilling={isAutoFilling}
                isExporting={isExporting}
                isPremium={isPremium}
                onPremiumGate={() => setShowPremiumGate(true)}
              />
            </div>
            <button
              onClick={() => {
                if (isPremium) {
                  setShowSimulation(s => !s);
                } else {
                  setShowPremiumGate(true);
                }
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all shrink-0 ${
                showSimulation
                  ? 'border-accent-blue bg-accent-blue/10 text-accent-blue'
                  : 'border-border/50 text-text-secondary hover:border-accent-blue/40 hover:text-accent-blue'
              }`}
              title="Monte Carlo Simulation (Premium)"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
              </svg>
              Simulate
              {!isPremium && (
                <svg className="w-3 h-3 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                </svg>
              )}
            </button>
          </div>

          {/* Monte Carlo Simulation Panel (Premium) */}
          {showSimulation && isPremium && (
            <SimulationPanel
              season={CURRENT_SEASON}
              onClose={() => setShowSimulation(false)}
            />
          )}

          {/* Bracket visualization */}
          <BracketView
            bracket={bracket}
            entries={entries}
            predictions={predictions}
            onPickWinner={handlePickWinner}
            onAnalyze={handleAnalyze}
            onAskKnewton={handleAskKnewton}
            onMascotBattle={handleMascotBattle}
            onPremiumGate={() => setShowPremiumGate(true)}
            isPremium={isPremium}
          />
        </>
      )}

      {/* Matchup Analyzer Modal */}
      {analyzerState && (
        <MatchupAnalyzerModal
          team1EntryId={analyzerState.team1Id}
          team2EntryId={analyzerState.team2Id}
          roundNumber={analyzerState.roundNumber}
          autoKnewton={analyzerState.autoKnewton}
          onClose={() => setAnalyzerState(null)}
          onPickWinner={handleAnalyzerPick}
        />
      )}

      {/* Mascot Battle Modal */}
      {battleState && (
        <MascotBattleModal
          team1EntryId={battleState.team1Id}
          team2EntryId={battleState.team2Id}
          onClose={() => setBattleState(null)}
          onPickWinner={handleBattlePick}
        />
      )}

      {/* Premium Gate Modal */}
      <PremiumGateModal
        isOpen={showPremiumGate}
        onClose={() => setShowPremiumGate(false)}
        title="Premium Bracket Features"
        description="Unlock advanced prediction models, AI analysis, and Monte Carlo simulations."
        features={[
          "Knewton AI matchup analysis per game",
          "Mascot Battle prediction model",
          "Custom strategy with tunable model weights",
          "Monte Carlo simulation (up to 50,000 runs)",
          "Compare strategies and find hidden value",
        ]}
      />

      {/* Hidden export image renderer (off-screen for capture) */}
      {showExportPreview && (
        <div style={{ position: 'fixed', left: '-9999px', top: 0, zIndex: -1 }}>
          <BracketExportImage
            ref={exportRef}
            bracket={bracket}
            entries={entries}
          />
        </div>
      )}
    </div>
  );
}

function PageHeader() {
  return (
    <div>
      <h1 className="text-2xl font-black text-text-primary">
        March Madness Bracket
      </h1>
      <p className="text-sm text-text-secondary mt-0.5">
        Build your bracket with AI-powered predictions
      </p>
    </div>
  );
}

function ConfidenceBadge({ score }) {
  // 0-10 scale: red < 4, yellow 4-6, green 7+
  let color = 'text-danger';
  let bg = 'bg-danger/10';
  if (score >= 7) {
    color = 'text-success';
    bg = 'bg-success/10';
  } else if (score >= 4) {
    color = 'text-warning';
    bg = 'bg-warning/10';
  }

  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${color} ${bg}`}>
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
      {score.toFixed(1)}
    </span>
  );
}

function BracketListPanel({ brackets, onCreate, onLoad, onDelete, onClone, onBack }) {
  const [newName, setNewName] = useState('');

  const handleCreate = () => {
    const name = newName.trim() || 'My Bracket';
    onCreate(name);
    setNewName('');
  };

  return (
    <div className="glass-card p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-text-primary">Your Brackets</h2>
        {onBack && (
          <button
            onClick={onBack}
            className="text-xs text-text-tertiary hover:text-accent-blue transition-colors flex items-center gap-1"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Bracket
          </button>
        )}
      </div>

      {/* Create new */}
      <div className="flex gap-2">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          placeholder="Bracket name..."
          className="input-glow flex-1 text-sm"
          maxLength={100}
        />
        <button onClick={handleCreate} className="btn-gradient px-4 py-2 text-sm font-semibold rounded-lg">
          Create Bracket
        </button>
      </div>

      {/* Existing brackets */}
      {brackets.length > 0 ? (
        <div className="space-y-2">
          {brackets.map(b => (
            <div
              key={b.id}
              className="flex items-center justify-between p-3 rounded-lg bg-bg-primary/40 border border-border/30 hover:border-accent-blue/30 transition-all"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onLoad(b.id)}
                    className="text-sm font-medium text-text-primary hover:text-accent-blue transition-colors text-left truncate"
                  >
                    {b.bracket_name}
                  </button>
                  {b.confidence_score != null && (
                    <ConfidenceBadge score={b.confidence_score} />
                  )}
                </div>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-[10px] text-text-tertiary">{b.total_picks}/63 picks</span>
                  <span className="text-[10px] text-text-tertiary capitalize">{b.strategy}</span>
                  {b.is_complete === 1 && (
                    <span className="text-[10px] text-success font-bold">COMPLETE</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => onClone(b.id)}
                  className="p-1.5 text-text-tertiary hover:text-accent-purple transition-colors"
                  title="Clone bracket"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
                <button
                  onClick={() => onDelete(b.id)}
                  className="p-1.5 text-text-tertiary hover:text-danger transition-colors"
                  title="Delete bracket"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-text-tertiary text-center py-4">
          No brackets yet. Create one to get started!
        </p>
      )}
    </div>
  );
}
